
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to add Identity endpoints to your app. Identity lets you use user information such as names and addresses to verify users and reduce fraud.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const APIVersion = makeShortcode("APIVersion");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Identity to your app' subtitle='Use Identity to verify user data' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use `}<a parentName="p" {...{
        "href": "/docs/api/products/identity/"
      }}>{`Identity`}</a>{` to retrieve identity data. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, you can skip ahead to `}<a parentName="p" {...{
        "href": "#matching-identity-data"
      }}>{`Matching identity data`}</a>{` (for `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{`) or `}<a parentName="p" {...{
        "href": "#fetching-identity-data"
      }}>{`Fetching identity data`}</a>{` (for `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{`).`}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` on the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <p>{`In the code samples below, you will need to replace `}<inlineCode parentName="p">{`PLAID_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID_SECRET`}</inlineCode>{` with your own keys, which you can obtain from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Dashboard`}</a>{`. These code samples also demonstrate starting up a server commonly used in each framework (such as Express or Flask).`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-in-link"
    }}>{`Create an Item in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <SampleCode.CreateLinkToken product='identity' />
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
    <h4 {...{
      "id": "get-a-persistent-access_token"
    }}>{`Get a persistent access_token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access `}<inlineCode parentName="p">{`Item`}</inlineCode>{` data and identify `}<inlineCode parentName="p">{`webhooks`}</inlineCode>{`, respectively. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely, and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API. `}</p>
    <h4 {...{
      "id": "matching-identity-data"
    }}>{`Matching Identity data`}</h4>
    <p>{`To match Identity data, call `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{`.`}</p>
    <p>{`If you are using `}<a parentName="p" {...{
        "href": "/docs/api/products/identity-verification/"
      }}>{`Identity Verification`}</a>{`, you can automatically match data from the linked account against data collected during the Identity Verification flow. To enable this setting, from the Identity Verification section of the Dashboard, access the template editor and on the "Setup" pane of the template, check the box under the "Financial Account Matching" header. If this option is selected, you should call `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` with only an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` to obtain match scores. `}</p>
    <p>{`If you are not using `}<a parentName="p" {...{
        "href": "/docs/api/products/identity-verification/"
      }}>{`Identity Verification`}</a>{`, you will need to send the identity information that you have on file and would like to match against, such as name, phone number, and address, as part of your call to `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{`. `}</p>
    <MDXCodeBlock title='/identity/match' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Omit user object if using identity verification / financial account matching
user = Plaid::IdentityMatchUser.new(
  legal_name: 'Jane Smith',
  phone_number: '+14155552671',
  email_address: 'jane.smith@example.com',
  address: Plaid::AddressDataNullableNoRequiredFields.new(
    street: '500 Market St',
    city: 'San Francisco',
    region: 'CA',
    postal_code: '94105',
    country: 'US'
  )
)

request = Plaid::IdentityMatchRequest.new(
  access_token: access_token,
  user: user
)

response = client.identity_match(request)
accounts = response.accounts

accounts.map do |account|
  name_score = account.legal_name&.score
  phone_score = account.phone_number&.score
  email_score = account.email_address&.score
  address_score = account.address&.score
end

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/identity/match \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": String,
   "user": {
       "phone_number": String,
       "legal_name": String,
       "email_address": String,
       "address": {
           "street": String,
           "region": String,
           "country": String,
           "city": String,
           "postal_code": String
       }
    }
}'

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request = {
  access_token: accessToken,
  // Omit user object if using Identity Verification / Financial Account Matching
  user: {
    legal_name: 'Jane Smith',
    phone_number: '+14155552671',
    email_address: 'jane.smith@example.com',
    address: {
      street: '500 Market St',
      city: 'San Francisco',
      region: 'CA',
      postal_code: '94105',
      country: 'US',
    },
  },
};

try {
  const response = await plaidClient.identityMatch(request);
  const accounts = response.data.accounts;
  for (const account of accounts) {
    const legalNameScore = account.legal_name?.score;
    const phoneScore = account.phone_number?.score;
    const emailScore = account.email_address?.score;
    const addressScore = account.address?.score;
  }
} catch (error) {
  // handle error
}

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// Match identity provided by client against bank/account identity

// Omit user object if using Identity Verification / Financial Account Matching
IdentityMatchUser user = new IdentityMatchUser()
    .legalName("Jane Smith")
    .phoneNumber("+14155552671")
    .emailAddress("jane.smith@example.com")
    .address(new AddressDataNullableNoRequiredFields()
        .street("500 Market St")
        .city("San Francisco")
        .region("CA")
        .postalCode("94105")
        .country("US"));

IdentityMatchRequest request = new IdentityMatchRequest()
    .accessToken(accessToken)
    .user(user);

Response<IdentityMatchResponse> response = plaidClient.identityMatch(request).execute();

List<AccountIdentityMatchScore> accounts = response.body().getAccounts();

for (AccountIdentityMatchScore account : accounts) {
  Integer legalNameScore = account.getLegalName() != null ? account.getLegalName().getScore() : null;
  Integer phoneNumberScore = account.getPhoneNumber() != null ? account.getPhoneNumber().getScore() : null;
  Integer emailAddressScore = account.getEmailAddress() != null ? account.getEmailAddress().getScore() : null;
  Integer addressScore = account.getAddress() != null ? account.getAddress().getScore() : null;

  // Compare scores to risk thresholds
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# Omit user object if using Identity Verification / Financial Account Matching
user = IdentityMatchUser(
    legal_name="Jane Smith",
    phone_number="+14155552671",
    email_address="jane.smith@example.com",
    address=AddressDataNullableNoRequiredFields(
        street="500 Market St",
        city="San Francisco",
        region="CA",
        postal_code="94105",
        country="US"
    )
)

request = IdentityMatchRequest(
    access_token=access_token,
    user=user
)

response = client.identity_match(request)
accounts = response['accounts']

for account in accounts:
    name_score = account['legal_name']['score'] if account['legal_name'] is not None else None
    phone_score = account['phone_number']['score'] if account['phone_number'] is not None else None
    email_score = account['email_address']['score'] if account['email_address'] is not None else None
    address_score = account['address']['score'] if account['address'] is not None else None

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// Omit user object if using Identity Verification / Financial Account Matching
user := plaid.IdentityMatchUser{}
user.SetLegalName("Jane Smith")
user.SetPhoneNumber("+14155552671")
user.SetEmailAddress("jane.smith@example.com")

address := plaid.AddressDataNullableNoRequiredFields{}
address.SetStreet("500 Market St")
address.SetCity("San Francisco")
address.SetRegion("CA")
address.SetPostalCode("94105")
address.SetCountry("US")
user.SetAddress(address)

request := plaid.NewIdentityMatchRequest(accessToken)
request.SetUser(user)

response, _, err := client.PlaidApi.IdentityMatch(ctx).IdentityMatchRequest(*request).Execute()
if err != nil {
    log.Fatal(err)
}

accounts := response.GetAccounts()
for _, account := range accounts {
    if legalName, ok := account.GetLegalNameOk(); ok {
        legalNameScore := legalName.GetScore()
        _ = legalNameScore
    }
    if phoneNumber, ok := account.GetPhoneNumberOk(); ok {
        phoneScore := phoneNumber.GetScore()
        _ = phoneScore
    }
    if emailAddress, ok := account.GetEmailAddressOk(); ok {
        emailScore := emailAddress.GetScore()
        _ = emailScore
    }
    if address, ok := account.GetAddressOk(); ok {
        addressScore := address.GetScore()
        _ = addressScore
    }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`// Omit user object if using Identity Verification / Financial Account Matching
user := plaid.IdentityMatchUser{}
user.LegalName = plaid.String("Jane Smith")
user.PhoneNumber = plaid.String("+14155552671")
user.EmailAddress = plaid.String("jane.smith@example.com")

address := plaid.AddressDataNullableNoRequiredFields{}
address.Street = plaid.String("500 Market St")
address.City = plaid.String("San Francisco")
address.Region = plaid.String("CA")
address.PostalCode = plaid.String("94105")
address.Country = plaid.String("US")
user.Address = &address

request := &plaid.IdentityMatchRequest{AccessToken: accessToken}
request.User = &user

response, err := client.Identity.Match(ctx, request)
if err != nil {
    log.Fatal(err)
}

accounts := response.GetAccounts()
for _, account := range accounts {
    if legalName := account.GetLegalName(); legalName != nil {
        legalNameScore := legalName.GetScore()
        _ = legalNameScore
    }
    if phoneNumber := account.GetPhoneNumber(); phoneNumber != nil {
        phoneScore := phoneNumber.GetScore()
        _ = phoneScore
    }
    if emailAddress := account.GetEmailAddress(); emailAddress != nil {
        emailScore := emailAddress.GetScore()
        _ = emailScore
    }
    if address := account.GetAddress(); address != nil {
        addressScore := address.GetScore()
        _ = addressScore
    }
}
`}</code></pre>
    </MDXCodeBlock>
    <p>{`The call to `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` will return a match score for each field that was evaluated. Typically, your threshold to accept the field as a match should be set to at least 70. For more details, see `}<a parentName="p" {...{
        "href": "/docs/identity/interpreting-identity-match"
      }}>{`Interpreting Identity Match Scores`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Identity Match sample response",
        "#Identity": true,
        "Match": true,
        "sample": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "BxBXxLj1m4HMXBm9WZZmCWVbPjX16EHwv99vp",
      "balances": {
        "available": null,
        "current": null,
        "iso_currency_code": null,
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "legal_name": {
        "score": 90,
        "is_nickname_match": true,
        "is_first_name_or_last_name_match": true,
        "is_business_name_detected": false
      },
      "phone_number": {
        "score": 100
      },
      "email_address": {
        "score": 100
      },
      "address": {
        "score": 100,
        "is_postal_code_match": true
      },
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
      "balances": {
        "available": null,
        "current": null,
        "iso_currency_code": null,
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "1111",
      "name": "Plaid Saving",
      "official_name": "Plaid Silver Standard 0.1% Interest Saving",
      "legal_name": {
        "score": 30,
        "is_first_name_or_last_name_match": false
      },
      "phone_number": {
        "score": 100
      },
      "email_address": null,
      "address": {
        "score": 100,
        "is_postal_code_match": true
      },
      "subtype": "savings",
      "type": "depository"
    }
  ],
  ...
}
`}</code></pre>
    <h4 {...{
      "id": "fetching-identity-data"
    }}>{`Fetching Identity data`}</h4>
    <p>{`If you are not using Identity Match, call `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{` to obtain Identity data. You will need to implement your own matching algorithm to determine whether the data returned matches the information that you have on file about the user. For more detailed information on the schema returned, see `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{`.`}</p>
    <MDXCodeBlock title='/identity/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# pull identity data for an item
request = Plaid::IdentityGetRequest.new({ access_token: access_token })
response = client.identity_get(request)
accounts = response.accounts
identities = accounts.map { |account| account.owners }.flatten
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/identity/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { IdentityGetRequest } = require('plaid');

// Pull Identity data for an Item
const request: IdentityGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.identityGet(request);
  const identities = response.data.accounts.flatMap(
    (account) => account.owners,
  );
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.IdentityGetRequest;
import com.plaid.client.model.IdentityGetResponse;

// Pull Identity data for an Item
IdentityGetRequest request = new IdentityGetRequest()
  .accessToken(accessToken);
Response<IdentityGetResponse> response = client().identityGet(request).execute();

List<AccountIdentity> accounts = response.body().getAccounts();

// Java 7
List<Owner> identities = new ArrayList<>();
for (AccountIdentity account : accounts) {
  identities.addAll(account.getOwners());
}

// Java 8 and above
List<Owner> identities = accounts.stream()
  .flatMap(account -> account.getOwners().stream())
  .collect(Collectors.toList());
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.identity_get_request import IdentityGetRequest

# pull identity data for an item
request = IdentityGetRequest(access_token=access_token)
response = client.identity_get(request)
accounts = response['accounts']
identities = []
for account in accounts:
    identities.extend(account['owners'])
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

identityGetResp, _, err := client.PlaidApi.IdentityGet(ctx).IdentityGetRequest(
  *plaid.NewIdentityGetRequest(accessToken),
).Execute()

accounts := identityGetResp.GetAccounts()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

identityGetResp, err := client.Identity.Get(ctx, &plaid.IdentityGetRequest{AccessToken: accessToken})

accounts := identityGetResp.GetAccounts()
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Example response data is below.`}</p>
    <APIVersion version='>2018-05-22' mdxType="APIVersion">
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "#Identity sample data",
          "#Identity": true,
          "sample": true,
          "data": true
        }}>{`{
  "accounts": [
    {
      "account_id": "BxBXxLj1m4HMXBm9WZZmCWVbPjX16EHwv99vp",
      "balances": {
        "available": 100,
        "current": 110,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "owners": [
        {
          "addresses": [
            {
              "data": {
                "city": "Malakoff",
                "country": "US",
                "postal_code": "14236",
                "region": "NY",
                "street": "2992 Cameron Road"
              },
              "primary": true
            },
            {
              "data": {
                "city": "San Matias",
                "country": "US",
                "postal_code": "93405-2255",
                "region": "CA",
                "street": "2493 Leisure Lane"
              },
              "primary": false
            }
          ],
          "emails": [
            {
              "data": "accountholder0@example.com",
              "primary": true,
              "type": "primary"
            },
            {
              "data": "accountholder1@example.com",
              "primary": false,
              "type": "secondary"
            }
          ],
          "names": ["Alberta Bobbeth Charleson"],
          "phone_numbers": [
            {
              "data": "1112223333",
              "primary": false,
              "type": "home"
            },
            {
              "data": "1112224444",
              "primary": false,
              "type": "work"
            },
            {
              "data": "1112225555",
              "primary": false,
              "type": "mobile1"
            }
          ]
        }
      ],
      "subtype": "checking",
      "type": "depository"
    }
  ],
  "item": {
    "available_products": ["balance", "credit_details", "investments"],
    "billed_products": [
      "assets",
      "auth",
      "identity",
      "liabilities",
      "transactions"
    ],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_3",
    "item_id": "eVBnVMp7zdTJLkRNr33Rs6zr7KNJqBFL9DrE6",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "3nARps6TOYtbACO"
}
`}</code></pre>
    </APIVersion>
    <APIVersion version='<2019-05-29' mdxType="APIVersion">
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "#Identity sample response",
          "#Identity": true,
          "sample": true,
          "response": true
        }}>{`{
  "accounts": [
    {
      "account_id": "vZ81n4yEkxS7kqGGdXJ9TE33DElgK7HWxN48J",
      "balances": {
        "available": 100,
        "current": 110,
        "limit": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "RvxEDr6W9zIraDnn164xfdmmWdQK6RtRjDrn5",
      "balances": {
        "available": 200,
        "current": 210,
        "limit": null
      },
      "mask": "1111",
      "name": "Plaid Saving",
      "official_name": "Plaid Silver Standard 0.1% Interest Saving",
      "subtype": "savings",
      "type": "depository"
    },
    {
      "account_id": "6j9VEz5R7aI8r4MMoP9xcnllAnE1BgfgJNMdA",
      "balances": {
        "available": null,
        "current": 1000,
        "limit": null
      },
      "mask": "2222",
      "name": "Plaid CD",
      "official_name": "Plaid Bronze Standard 0.2% Interest CD",
      "subtype": "cd",
      "type": "depository"
    },
    {
      "account_id": "XvjmWa6y94ImnwaagdJrTQJJNQrRxZsdgVzez",
      "balances": {
        "available": null,
        "current": 410,
        "limit": 2000
      },
      "mask": "3333",
      "name": "Plaid Credit Card",
      "official_name": "Plaid Diamond 12.5% APR Interest Credit Card",
      "subtype": "credit card",
      "type": "credit"
    },
    {
      "account_id": "DAdNDM6PvgTPB1ooLxvjUVDDBVykZGiveV96A",
      "balances": {
        "available": 43200,
        "current": 43200,
        "limit": null
      },
      "mask": "4444",
      "name": "Plaid Money Market",
      "official_name": "Plaid Platinum Standard 1.85% Interest Money Market",
      "subtype": "money market",
      "type": "depository"
    }
  ],
  "identity": {
    "addresses": [
      {
        "accounts": [
          "Plaid Checking 0000",
          "Plaid Saving 1111",
          "Plaid CD 2222"
        ],
        "data": {
          "city": "Malakoff",
          "state": "NY",
          "street": "2992 Cameron Road",
          "zip": "14236"
        },
        "primary": true
      },
      {
        "accounts": ["Plaid Credit Card 3333"],
        "data": {
          "city": "San Matias",
          "state": "CA",
          "street": "2493 Leisure Lane",
          "zip": "93405-2255"
        },
        "primary": false
      }
    ],
    "emails": [
      {
        "data": "accountholder0@example.com",
        "primary": true,
        "type": "primary"
      },
      {
        "data": "accountholder1@example.com",
        "primary": false,
        "type": "secondary"
      },
      {
        "data": "extraordinarily.long.email.username.123456@reallylonghostname.com",
        "primary": false,
        "type": "other"
      }
    ],
    "names": ["Alberta Bobbeth Charleson"],
    "phone_numbers": [
      {
        "data": "1112223333",
        "primary": false,
        "type": "home"
      },
      {
        "data": "1112224444",
        "primary": false,
        "type": "work"
      },
      {
        "data": "1112225555",
        "primary": false,
        "type": "mobile1"
      }
    ]
  },
  "item": {
    "available_products": ["balance"],
    "billed_products": [
      "assets",
      "auth",
      "identity",
      "investments",
      "transactions"
    ],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_11",
    "item_id": "XvjmWa6y94ImnwaagdJrTQJJWdnqjZCdVn5xm",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "ugcZSNLf0yZQliS"
}
`}</code></pre>
    </APIVersion>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that incorporates Identity, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that fetches Identity data in order to verify identity prior to a funds transfer. The Identity code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L133-L176"
      }}>{`items.js`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-in-link",
        level: 4,
        title: "Create an Item in Link",
      },
{
        id: "get-a-persistent-access_token",
        level: 4,
        title: "Get a persistent access_token",
      },
{
        id: "matching-identity-data",
        level: 4,
        title: "Matching Identity data",
      },
{
        id: "fetching-identity-data",
        level: 4,
        title: "Fetching Identity data",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
